import { getChatbotMessageLexer, ChatbotMessageTokenType } from './chatbotMessageLexer.js';
import { INCOMPLETE_DRAFT_MARKER, COMPLETE_DRAFT_MARKER } from './filterDiffTags.js';
import * as levenshtein from 'fastest-levenshtein';

const NORMALIZATION_MAPS = {
  // Smart quotes to regular quotes
  SMART_QUOTES: {
    "“": '"',
    "”": '"',
    "‘": "'",
    "’": "'"
  },
  // Other typographic characters
  TYPOGRAPHIC: {
    "…": "...",
    "—": "-",
    "–": "-",
    " ": " "
  }
};
function normalizeString(str) {
  let normalized = str;
  for (const [smart, regular] of Object.entries(NORMALIZATION_MAPS.SMART_QUOTES)) {
    normalized = normalized.replace(new RegExp(smart, "g"), regular);
  }
  for (const [typographic, regular] of Object.entries(NORMALIZATION_MAPS.TYPOGRAPHIC)) {
    normalized = normalized.replace(new RegExp(typographic, "g"), regular);
  }
  normalized = normalized.replace(/\s+/g, " ");
  return normalized.trim();
}
function getSimilarity(original, search) {
  if (search === "") {
    return 0;
  }
  const normalizedOriginal = normalizeString(original);
  const normalizedSearch = normalizeString(search);
  if (normalizedOriginal === normalizedSearch) {
    return 1;
  }
  const dist = levenshtein.distance(normalizedOriginal, normalizedSearch);
  const maxLength = Math.max(normalizedOriginal.length, normalizedSearch.length);
  return 1 - dist / maxLength;
}
const getStrictDiffs = (node, incomplete = false) => {
  if (node.type !== "diffs") return null;
  const validChildren = node.children.filter((child) => child.type === "diff");
  if (!incomplete && validChildren.length === 0) return null;
  return {
    type: "diffs",
    children: validChildren.length > 0 ? validChildren : node.children,
    incomplete
  };
};
const closeStackTill = (stack, index, incomplete = false) => {
  while (stack.length > index) {
    let node = stack.pop() ?? null;
    switch (node?.type) {
      case "diff":
        node = getStrictDiff(node, incomplete);
        break;
      case "diffs":
        node = getStrictDiffs(node, incomplete);
        break;
    }
    if (!node) continue;
    if (incomplete && node.type !== "text") node = {
      ...node,
      incomplete
    };
    if (stack.length === 0) {
      return node;
    }
    const prevNode = stack[stack.length - 1];
    if (prevNode.type === "text") continue;
    prevNode.children.push(node);
  }
  return null;
};
const containsOnlyTextNodes = (node) => {
  if (node.type === "text") return true;
  if (!node.children || node.children.length === 0) return false;
  return node.children.every((child) => child.type === "text");
};
const getTextContentFromChildren = (node) => {
  if (node.type === "text") return node.content;
  if (!node.children || node.children.length === 0) return "";
  return node.children.map((child) => getTextContentFromChildren(child)).join("");
};
const getStrictDiff = (node, incomplete = false) => {
  if (node.type !== "diff") return null;
  if (!incomplete && (!node.children || node.children.length === 0)) return null;
  const strictChildren = {
    search: null,
    location: null,
    replace: null
  };
  for (const child of node.children) {
    if (child.type !== "search" && child.type !== "location" && child.type !== "replace") continue;
    const existingChild = strictChildren[child.type];
    if (existingChild) {
      const textContent = getTextContentFromChildren(child);
      const existingTextContent = getTextContentFromChildren(existingChild);
      const similarity = getSimilarity(textContent, existingTextContent);
      const partialSimilarity = getSimilarity(textContent.substring(0, Math.min(textContent.length, existingTextContent.length)), existingTextContent.substring(0, Math.min(textContent.length, existingTextContent.length)));
      if (similarity > 0.8 || partialSimilarity > 0.9) {
        strictChildren[child.type] = child;
        continue;
      }
      existingChild.children.push(...child.children);
      existingChild.textContent = getTextContentFromChildren(existingChild);
    } else {
      strictChildren[child.type] = child;
    }
  }
  if (!incomplete && (!strictChildren.search || !strictChildren.location || !strictChildren.replace)) {
    return null;
  }
  return {
    type: "diff",
    children: [
      strictChildren.location,
      strictChildren.search,
      strictChildren.replace
    ].filter((child) => child !== null).sort((a, b) => (a.incomplete ? 1 : 0) - (b.incomplete ? 1 : 0)),
    incomplete
  };
};
const parseChatbotMessageNodes = (message) => {
  const lexer = getChatbotMessageLexer();
  lexer.reset(message);
  const rootNodes = [];
  const nodeStack = [];
  let token = void 0;
  do {
    token = lexer.next();
    const currentNode = nodeStack.length > 0 ? nodeStack[nodeStack.length - 1] : null;
    const previousNode = currentNode && currentNode.type !== "text" ? currentNode.children[currentNode.children.length - 1] : rootNodes[rootNodes.length - 1];
    if (!token) break;
    switch (token.type) {
      case ChatbotMessageTokenType.CodeBlock:
      case ChatbotMessageTokenType.WS:
      case ChatbotMessageTokenType.Char:
      case ChatbotMessageTokenType.OtherContent:
      case ChatbotMessageTokenType.Content: {
        if (currentNode?.type === "text") {
          currentNode.content += token.value;
        } else if (previousNode?.type === "text") {
          previousNode.content += token.value;
        } else {
          if (currentNode) currentNode.children.push({
            type: "text",
            content: token.value
          });
          else rootNodes.push({
            type: "text",
            content: token.value
          });
        }
        if (currentNode?.type === "replace" || currentNode?.type === "location" || currentNode?.type === "search") currentNode.textContent += token.value;
        break;
      }
      case ChatbotMessageTokenType.DiffsStart:
        nodeStack.push({
          type: "diffs",
          children: []
        });
        break;
      case ChatbotMessageTokenType.DiffsStartInterrupt:
      case ChatbotMessageTokenType.DiffsStartNested: {
        const parentIndex = nodeStack.findLastIndex((node) => node.type === "diffs");
        if (parentIndex === -1) {
          nodeStack.push({
            type: "diffs",
            children: []
          });
          break;
        }
        const parentNode = nodeStack[parentIndex];
        if (containsOnlyTextNodes(parentNode)) {
          const textContent = getTextContentFromChildren(parentNode);
          if (textContent.trim().length > 0) {
            closeStackTill(nodeStack, parentIndex);
            const newParent = nodeStack.length > 0 ? nodeStack[nodeStack.length - 1] : null;
            const target = newParent && newParent.type !== "text" ? newParent.children : rootNodes;
            target.push({
              type: "text",
              content: textContent
            });
            nodeStack.push({
              type: "diffs",
              children: []
            });
          }
        }
        break;
      }
      case ChatbotMessageTokenType.DiffsEndInterrupt:
      case ChatbotMessageTokenType.DiffsEnd: {
        const firstDiffsIndex = nodeStack.findIndex((node2) => node2.type === "diffs");
        if (firstDiffsIndex === -1) {
          break;
        }
        const node = closeStackTill(nodeStack, firstDiffsIndex);
        if (node) rootNodes.push(node);
        break;
      }
      case ChatbotMessageTokenType.DiffStart:
        nodeStack.push({
          type: "diff",
          children: []
        });
        break;
      case ChatbotMessageTokenType.DiffStartInterrupt:
      case ChatbotMessageTokenType.DiffStartNested: {
        const parentIndex = nodeStack.findLastIndex((node) => node.type === "diff");
        if (parentIndex !== -1) {
          const parentNode = nodeStack[parentIndex];
          if (containsOnlyTextNodes(parentNode)) {
            const textContent = getTextContentFromChildren(parentNode);
            if (textContent.trim().length > 0) {
              closeStackTill(nodeStack, parentIndex);
            }
          }
        }
        nodeStack.push({
          type: "diff",
          children: []
        });
        break;
      }
      case ChatbotMessageTokenType.DiffEndInterrupt:
      case ChatbotMessageTokenType.DiffEnd: {
        const diffs = nodeStack.reduce((acc, node, index) => node.type === "diff" ? [
          ...acc,
          {
            node,
            index
          }
        ] : acc, []);
        for (let i = diffs.length - 1; i >= 0; i--) {
          const { node, index } = diffs[i];
          const prevDiff = diffs[i - 1];
          if (prevDiff) {
            const textContent = getTextContentFromChildren(node);
            const prevTextContent = getTextContentFromChildren(prevDiff.node);
            const fullSimilarity = getSimilarity(textContent, prevTextContent);
            const partialSimilarity = getSimilarity(textContent.substring(0, Math.min(textContent.length, prevTextContent.length)), prevTextContent.substring(0, Math.min(textContent.length, prevTextContent.length)));
            if (fullSimilarity > 0.8 || partialSimilarity > 0.9) {
              continue;
            }
          }
          const closedNode = closeStackTill(nodeStack, index);
          if (closedNode) rootNodes.push(closedNode);
        }
        break;
      }
      case ChatbotMessageTokenType.ReplaceStartNested:
      case ChatbotMessageTokenType.ReplaceStart:
      case ChatbotMessageTokenType.SearchStartNested:
      case ChatbotMessageTokenType.SearchStart:
      case ChatbotMessageTokenType.LocationStartNested:
      case ChatbotMessageTokenType.LocationStart: {
        let type;
        if (token.type.includes("location")) type = "location";
        else if (token.type.includes("search")) type = "search";
        else if (token.type.includes("replace")) type = "replace";
        else break;
        const isNested = token.type.includes("Nested");
        if (isNested) {
          const parentIndex = nodeStack.findLastIndex((node) => node.type === type);
          if (parentIndex !== -1) {
            closeStackTill(nodeStack, parentIndex);
          }
        }
        nodeStack.push({
          type,
          children: [],
          textContent: ""
        });
        break;
      }
      case ChatbotMessageTokenType.SearchStartInterrupt: {
        const nearestReplaceOrLocationIndex = nodeStack.findLastIndex((node) => node.type === "replace" || node.type === "location");
        if (nearestReplaceOrLocationIndex !== -1) {
          closeStackTill(nodeStack, nearestReplaceOrLocationIndex);
        }
        nodeStack.push({
          type: "search",
          children: [],
          textContent: ""
        });
        break;
      }
      case ChatbotMessageTokenType.ReplaceStartInterrupt: {
        const nearestSearchOrLocationIndex = nodeStack.findLastIndex((node) => node.type === "search" || node.type === "location");
        if (nearestSearchOrLocationIndex !== -1) {
          closeStackTill(nodeStack, nearestSearchOrLocationIndex);
        }
        nodeStack.push({
          type: "replace",
          children: [],
          textContent: ""
        });
        break;
      }
      case ChatbotMessageTokenType.LocationStartInterrupt: {
        const nearestSearchOrReplaceIndex = nodeStack.findLastIndex((node) => node.type === "search" || node.type === "replace");
        if (nearestSearchOrReplaceIndex !== -1) {
          closeStackTill(nodeStack, nearestSearchOrReplaceIndex);
        }
        nodeStack.push({
          type: "location",
          children: [],
          textContent: ""
        });
        break;
      }
      case ChatbotMessageTokenType.LocationEnd: {
        const firstLocationIndex = nodeStack.findIndex((node2) => node2.type === "location");
        if (firstLocationIndex === -1) {
          break;
        }
        const node = closeStackTill(nodeStack, firstLocationIndex);
        if (node) rootNodes.push(node);
        break;
      }
      case ChatbotMessageTokenType.SearchEnd: {
        const firstSearchIndex = nodeStack.findIndex((node2) => node2.type === "search");
        if (firstSearchIndex === -1) {
          break;
        }
        const node = closeStackTill(nodeStack, firstSearchIndex);
        if (node) rootNodes.push(node);
        break;
      }
      case ChatbotMessageTokenType.ReplaceEnd: {
        const firstReplaceIndex = nodeStack.findIndex((node2) => node2.type === "replace");
        if (firstReplaceIndex === -1) {
          break;
        }
        const node = closeStackTill(nodeStack, firstReplaceIndex);
        if (node) rootNodes.push(node);
        break;
      }
      case ChatbotMessageTokenType.ContinueMarker: {
        const nearestSearchOrLocationOrReplaceIndex = nodeStack.findLastIndex((node) => node.type === "search" || node.type === "location" || node.type === "replace");
        if (nearestSearchOrLocationOrReplaceIndex !== -1) {
          const node = nodeStack[nearestSearchOrLocationOrReplaceIndex];
          let type;
          if (node.type.includes("location")) type = "location";
          else if (node.type.includes("search")) type = "search";
          else if (node.type.includes("replace")) type = "replace";
          else break;
          closeStackTill(nodeStack, nearestSearchOrLocationOrReplaceIndex);
          nodeStack.push({
            type,
            children: [],
            textContent: ""
          });
        }
      }
    }
  } while (token);
  if (nodeStack.length > 0) {
    const node = closeStackTill(nodeStack, 0, true);
    if (node) rootNodes.push(node);
  }
  return rootNodes;
};
const formatNode = (node, depth = 0) => {
  const indent = "  ".repeat(depth);
  if (node.type === "text") return `${indent}${node.content}`;
  const tagName = node.type;
  const tagStart = `<${tagName}>`;
  const tagEnd = `</${tagName}>`;
  const addNewLines = node.type === "diffs" || node.type === "diff";
  const children = node.children.map((child) => formatNode(child, addNewLines ? depth + 1 : 0));
  const content = children.join(addNewLines ? "\n" : "");
  const contentWithNewLines = addNewLines ? `
${content}` : content;
  let text = `${indent}${tagStart}${contentWithNewLines}`;
  if (!node.incomplete) text += `${addNewLines ? `
${indent}` : ""}${tagEnd}`;
  return text;
};
const formatChatbotMessageNodes = (nodes) => {
  return nodes.map(formatNode).join("\n");
};
const simplifiedFormatChatbotMessageNodes = (nodes, markers = {
  completeDiff: COMPLETE_DRAFT_MARKER,
  incompleteDiff: INCOMPLETE_DRAFT_MARKER
}) => {
  return nodes.map((node) => {
    if (node.type === "text") return node.content;
    if (node.type === "diffs") return node.incomplete ? markers.incompleteDiff : markers.completeDiff;
    else return "";
  }).filter((node) => node !== "").join("\n");
};
const findNodesRecursively = (nodes, type) => {
  return nodes.reduce((acc, node) => {
    if (node.type === type) acc.push(node);
    if (node.type !== "text") acc.push(...findNodesRecursively(node.children, type));
    return acc;
  }, []);
};
const retrieveChatbotDiffs = (nodes) => {
  const diffs = [];
  const diffNodes = findNodesRecursively(nodes, "diff");
  for (const [index, diffNode] of diffNodes.entries()) {
    if (diffNode.type !== "diff" || diffNode.incomplete) continue;
    const location = diffNode.children.find((child) => child.type === "location");
    const search = diffNode.children.find((child) => child.type === "search");
    const replace = diffNode.children.find((child) => child.type === "replace");
    if (!location || !search || !replace) continue;
    const diff = {
      index,
      location: location.textContent,
      search: {
        regex: false,
        value: search.textContent
      },
      replace: {
        regex: false,
        value: replace.textContent
      }
    };
    diffs.push(diff);
  }
  return diffs;
};
const isIncompleteMessage = (nodes) => {
  return nodes.some((node) => node.type !== "text" && (node.incomplete || isIncompleteMessage(node.children)));
};
const parseChatbotMessage = (message, markers = {
  completeDiff: COMPLETE_DRAFT_MARKER,
  incompleteDiff: INCOMPLETE_DRAFT_MARKER
}) => {
  const nodes = parseChatbotMessageNodes(message);
  return {
    diffs: retrieveChatbotDiffs(nodes),
    message: formatChatbotMessageNodes(nodes),
    simplifiedMessage: simplifiedFormatChatbotMessageNodes(nodes, markers),
    isIncomplete: isIncompleteMessage(nodes)
  };
};

export { formatChatbotMessageNodes, getSimilarity, isIncompleteMessage, parseChatbotMessage, parseChatbotMessageNodes, retrieveChatbotDiffs, simplifiedFormatChatbotMessageNodes };
//# sourceMappingURL=parseChatbotMessage.js.map
